import { useState, useEffect, useContext } from "react";
import { useTranslation } from "react-i18next";
import Select from "react-select";
import {
  AddButton,
  CancelButton,
} from "../addClientModal/addClientModal.styles";
import { UserContext } from "../../../contexts/user.context";
import { toast } from "react-toastify";
import { Get, handleError } from "../../../utils/fetch.util";
import { useParams } from "react-router-dom";

interface SecurityTask {
  name: string;
  description: string;
  start_time: string;
  end_time: string;
  security_post?: {
    site_id: number;
    id: number;
    name: string;
  };
  point?: {
    point_id: number;
  };
  days_of_week?: string;
  security_post_id?: number;
  point_id?: number;
  task_type: string;
  interval?: number;
  service_id: number;
}

interface AddSecurityTaskModalProps {
  isShown: boolean;
  closeModal: () => void;
  onSubmit: (data: SecurityTask) => Promise<void>;
  isLoading: boolean;
  initialData?: Partial<SecurityTask> | null;
}

const daysList = [
  "Monday",
  "Tuesday",
  "Wednesday",
  "Thursday",
  "Friday",
  "Saturday",
  "Sunday",
];

const AddSecurityTaskModal: React.FC<AddSecurityTaskModalProps> = ({
  isShown,
  closeModal,
  onSubmit,
  isLoading,
  initialData,
}) => {
  const { securityPostId } = useParams();
  const { t } = useTranslation();
  const [form, setForm] = useState<SecurityTask>({
    name: "",
    description: "",
    start_time: "08:00",
    end_time: "18:00",
    days_of_week: "Monday",
    security_post_id: Number(securityPostId || initialData?.security_post_id),
    point_id: 0,
    task_type: "interval",
    interval: 1,
    service_id: 0,
  });
  const [securityPlansOptions, setSecurityPlansOptions] = useState([]);
  const [selectedDays, setSelectedDays] = useState<string[]>(["Monday"]);
  const [siteId, setSiteId] = useState<number | null>(null);
  const [pointOptions, setPointOptions] = useState<any[]>([]);

  const { userData, getValidToken } = useContext(UserContext);

  const siteOptions =
    userData[0]?.user_sites_relationShip?.map((site: any) => {
      return {
        value: site.site_id,
        label: site.site.site_name,
      };
    }) || [];

  const servicesOptions =
    userData[0]?.asdc_client_contract?.client_service_relationship?.map(
      (service: any) => ({
        value: service.asdc_client_service.asdc_client_service_id,
        label: service.asdc_client_service.asdc_client_service_name,
      })
    ) || [];

  const UserServicesOptions =
    userData[0]?.userServices?.map((service: any) => {
      return {
        value: service?.asdc_client_service_id,
        label: service?.asdc_client_service_name,
      };
    }) || [];

  useEffect(() => {
    const getPoints = async () => {
      const res = await Get(
        `points/site-points?site_id=${(initialData ? initialData?.security_post?.site_id : siteId) || 0}`, getValidToken()
      );

      if (res.error || !res.data) {
        handleError(res);
        return;
      }
      setPointOptions(
        res.data.sitePoints.map((point: any) => ({
          value: point.point_id,
          label: point.point_name + " - " + point.serial,
        }))
      );
    };
    (initialData ? initialData?.security_post?.site_id : siteId) && getPoints();
  }, [initialData?.security_post?.site_id, siteId]);

  useEffect(() => {
    const getSecurityPlans = async () => {
      const query: string[] = [];
      const res = await Get(
        `security-post/get-all-posts`,
        getValidToken()
      );
      if (res?.error || !res.data) {
        handleError(res);
        return;
      }
      setSecurityPlansOptions(
        res.data.map((plan: any) => ({
          value: plan.id,
          label: plan.name,
        }))
      );
    };

    getSecurityPlans();
  }, []);

  useEffect(() => {
    if (initialData) {
      setForm({
        ...form,
        ...initialData,
        days_of_week: initialData.days_of_week || "Monday",
        interval: initialData.interval || 1,
        point_id: initialData.point?.point_id || 0,
        security_post_id: initialData.security_post?.id || 0,
      });
      setSelectedDays(
        initialData.days_of_week
          ? initialData.days_of_week.split(",")
          : ["Monday"]
      );
    }

    // eslint-disable-next-line
  }, [initialData, isShown]);

  const handleChange = (
    e: React.ChangeEvent<HTMLInputElement | HTMLSelectElement>
  ) => {
    const { name, value } = e.target;
    setForm((prev) => ({ ...prev, [name]: value }));
  };

  const handleSubmit = (e: React.FormEvent) => {
    e.preventDefault();
    if(initialData) {
      onSubmit({ ...form, security_post_id: form.security_post?.id, days_of_week: selectedDays.join(",") });
    } else {
      onSubmit({ ...form, security_post_id: form.security_post_id, days_of_week: selectedDays.join(",") });
    }
  };

  if (!isShown) return null;

  return (
    <div className="modal-overlay fixed inset-0 z-[99999] flex items-center justify-center bg-black bg-opacity-40">
      <div className="bg-white rounded-xl shadow-lg w-full max-w-lg relative animate-fadeInUp p-0">
        {/* Header */}
        <div className="flex items-center justify-between border-b px-6 py-4">
          <div className="text-xl font-bold">
            {initialData ? t("edit_task") : t("add_task")}
          </div>
          <button
            type="button"
            className="text-gray-500 hover:text-red-500 transition"
            onClick={() => {
              closeModal();
              setForm({
                name: "",
                description: "",
                start_time: "08:00",
                end_time: "18:00",
                days_of_week: "Monday",
                task_type: "interval",
                interval: 1,
                service_id: 0,
              });
              setSelectedDays(["Monday"]);
              setSiteId(null);
              setPointOptions([]);
            }}
            disabled={isLoading}
            aria-label="Close"
          >
            ×
          </button>
        </div>
        {/* Form */}
        <form onSubmit={handleSubmit} className="flex flex-col gap-4 px-6 py-6">
          <div>
            <label className="block font-medium mb-1">{t("name")}</label>
            <input
              name="name"
              value={form.name}
              onChange={handleChange}
              className="border w-full p-2 rounded-md"
              required
            />
          </div>
          <div>
            <label className="block font-medium mb-1">{t("description")}</label>
            <input
              name="description"
              value={form.description}
              onChange={handleChange}
              className="border w-full p-2 rounded-md"
              required
            />
          </div>
          <div className="flex gap-2">
            <div className="w-1/2">
              <label className="block font-medium mb-1">
                {t("start_time")}
              </label>
              <input
                name="start_time"
                type="time"
                value={form.start_time}
                onChange={handleChange}
                className="border w-full p-2 rounded-md"
                required
              />
            </div>
            <div className="w-1/2">
              <label className="block font-medium mb-1">{t("end_time")}</label>
              <input
                name="end_time"
                type="time"
                value={form.end_time}
                onChange={handleChange}
                className="border w-full p-2 rounded-md"
                required
              />
            </div>
          </div>
          <div className="flex gap-2">
            {!initialData ? (
              <>
                <div className="w-1/2">
                  <label className="block font-medium mb-1">{t("site")}</label>
                  <Select
                    classNamePrefix="react-select"
                    options={siteOptions}
                    value={siteOptions.find((site: any) => site.value === siteId)}
                    onChange={(selected) =>
                      setSiteId(selected ? selected.value : null)
                    }
                    placeholder={t("select_site")}
                    isClearable
                  />
                </div>
              </>
            ) : (<>
            </>)
            }
            <div className="w-1/2">
              <label className="block font-medium mb-1">{t("point")}</label>
              <Select
                classNamePrefix="react-select"
                options={pointOptions}
                value={pointOptions.find(
                  (point: any) => point.value === (form.point_id ? form.point_id : form?.point?.point_id)
                )}
                onChange={(selected) => {
                  setForm((prev) => ({
                    ...prev,
                    point_id: selected ? (selected as any).value : undefined,
                  }))
                }
                }
                placeholder={t("select_point")}
                isClearable
              />
            </div>
          </div>
          {!initialData && (<div className="w-full">
            <label className="block font-medium mb-1">{t("security_post")}</label>
            <Select
              classNamePrefix="react-select"
              options={securityPlansOptions}
              value={securityPlansOptions.find(
                (plan: any) => plan.value === (form.security_post_id ? form.security_post_id : (initialData as any)?.security_post?.id)
              )}
              onChange={(selected) => {
                setForm((prev) => ({
                  ...prev,
                  security_post_id: selected ? (selected as any).value : undefined,
                }))
              }
              }
              placeholder={t("select_security_post")}
              isClearable
            />
          </div>)}
          <div className="flex gap-2">
            <div className="w-1/2">
              <label className="block font-medium mb-1">{t("service")}</label>
              <Select
                options={UserServicesOptions}
                onChange={(e, opt) => {
                  if (
                    opt.action === "remove-value" &&
                    !UserServicesOptions.map(
                      (serv: any) => serv.value
                    ).includes(opt.removedValue?.value)
                  ) {
                    toast.error(t("you_cant_delete_this_service"));
                    return;
                  }
                  setForm((prev) => ({
                    ...prev,
                    service_id: (e as any)?.value,
                  }));
                }}
                value={servicesOptions.find(
                  (service: any) => service.value === initialData?.service_id
                )}
                placeholder={t("select_service")}
                required
              />
            </div>
            <div className="w-1/2">
              <label className="block font-medium mb-1">{t("task_type")}</label>
              <Select
                classNamePrefix="react-select"
                options={[
                  { value: "interval", label: t("interval") },
                  { value: "weekly", label: t("weekly") },
                ]}
                value={{ value: form.task_type, label: t(form.task_type) }}
                onChange={(selected) => {
                  setForm((prev) => ({
                    ...prev,
                    task_type: (selected as any).value,
                    interval: selected?.value === "interval" ? 1 : undefined,
                    days_of_week:
                      selected?.value === "weekly" ? "Monday" : undefined,
                  }));
                }}
                placeholder={t("select_task_type")}
                isClearable={false}
              />
            </div>
          </div>
          {form.task_type === "weekly" && (
            <div>
              <label className="block font-medium mb-1">
                {t("days_of_week")}
              </label>
              <Select
                isMulti
                classNamePrefix="react-select"
                options={daysList.map((day) => ({
                  value: day,
                  label: t(day.toLowerCase()),
                }))}
                value={selectedDays.map((day) => ({
                  value: day,
                  label: t(day.toLowerCase()),
                }))}
                onChange={(selected) => {
                  const days = (selected as any[]).map((opt) => opt.value);
                  setSelectedDays(days);
                  setForm((prev) => ({
                    ...prev,
                    days_of_week: days.join(","),
                  }));
                }}
                placeholder={t("select_days")}
              />
            </div>
          )}
          {form.task_type === "interval" && (
            <div>
              <label className="block font-medium mb-1">{t("interval")}</label>
              <input
                name="interval"
                type="number"
                value={form.interval || ""}
                onChange={handleChange}
                className="border w-full p-2 rounded-md"
                required
                min={1}
              />
            </div>
          )}
          <div className="flex justify-end gap-2 mt-4">
            <AddButton
              type="submit"
              isLoading={isLoading}
              disabled={isLoading || !form.name ? true : false}
            >
              {isLoading ? t("saving") : initialData ? t("save") : t("add")}
            </AddButton>
            <CancelButton
              type="button"
              onClick={() => {
                closeModal();
                setForm({
                  name: "",
                  description: "",
                  start_time: "08:00",
                  end_time: "18:00",
                  days_of_week: "Monday",
                  security_post_id: Number(securityPostId),
                  point_id: 0,
                  task_type: "interval",
                  interval: 1,
                  service_id: 0,
                });
                setSelectedDays(["Monday"]);
                setSiteId(null);
                setPointOptions([]);
              }}
              disabled={isLoading}
            >
              {t("cancel")}
            </CancelButton>
          </div>
        </form>
      </div >
    </div >
  );
};

export default AddSecurityTaskModal;
